import React from 'react';
import classnames from 'classnames';
import IconGlobe from '@scramble/design-system/icons/globe-01.svg';
import { getFlagURL } from '@scramble/utils';
import { Button } from '../../components/Buttons';
import {
  FloatingPortal,
  autoUpdate,
  flip,
  offset,
  shift,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
} from '@floating-ui/react';
import type { LanguageSwitcherProps } from './types';
import { LOCALES } from './locales';
import IconCheck from '@scramble/design-system/icons/check.svg';
import s from './index.module.scss';

export const LanguageSwitcher = ({
  currentLocale,
  locales = LOCALES,
  onLocaleChange,
}: LanguageSwitcherProps) => {
  const [open, setOpen] = React.useState(false);

  const { refs, floatingStyles, context } = useFloating({
    open,
    onOpenChange: setOpen,
    placement: 'bottom-end',
    whileElementsMounted: autoUpdate,
    middleware: [offset(4), flip({ padding: 8 }), shift({ padding: 8 })],
  });

  const click = useClick(context);
  const dismiss = useDismiss(context);

  const { getReferenceProps, getFloatingProps } = useInteractions([click, dismiss]);

  const handleSelect = (locale: string) => {
    onLocaleChange?.(locale);
    setOpen(false);
  };

  return (
    <>
      <span ref={refs.setReference} style={{ display: 'inline-flex' }} {...getReferenceProps()}>
        <Button theme="neutral" appearance="tonal" size="md" iconUrl={IconGlobe} />
      </span>
      {open && (
        <FloatingPortal>
          <div
            ref={refs.setFloating}
            style={floatingStyles}
            className={s.menu}
            {...getFloatingProps()}
          >
            {locales.map((locale) => (
              <button
                key={locale.code}
                type="button"
                className={classnames(s.item, currentLocale === locale.code && s.item_active)}
                onClick={() => handleSelect(locale.code)}
              >
                <img
                  className={s.icon_flag}
                  src={getFlagURL(locale.countryCode)}
                  alt={locale.countryCode}
                  width={20}
                  height={20}
                />
                <span className="f-body-md">{locale.label}</span>
                <img className={s.icon_check} src={IconCheck} alt="active" width={20} height={20} />
              </button>
            ))}
          </div>
        </FloatingPortal>
      )}
    </>
  );
};